import React, { useState, useEffect } from 'react';
import {
  MessageSquare,
  Send,
  CheckCircle2,
  Copy,
  Sparkles,
  Smartphone,
  Settings,
  RefreshCw,
  Zap,
  ExternalLink,
  ShieldCheck,
  Code,
  AlertCircle,
  X,
  Bot,
  Globe
} from 'lucide-react';
import { CompleteKundali } from '../types/kundali';
import { formatDateTime } from '../utils/dateUtils';

interface WhatsAppIntegrationModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentKundali?: CompleteKundali | null;
}

interface ChatMessage {
  id: string;
  sender: 'user' | 'bot';
  text: string;
  time: string;
}

interface WhatsAppStatus {
  isConfigured: boolean;
  webhookUrl: string;
  verifyToken: string;
  phoneNumberIdConfigured: boolean;
  totalLogs: number;
}

export const WhatsAppIntegrationModal: React.FC<WhatsAppIntegrationModalProps> = ({
  isOpen,
  onClose,
  currentKundali
}) => {
  const [activeTab, setActiveTab] = useState<'sandbox' | 'config' | 'logs'>('sandbox');
  const [status, setStatus] = useState<WhatsAppStatus | null>(null);
  const [inputMsg, setInputMsg] = useState('');
  const [loading, setLoading] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [logs, setLogs] = useState<any[]>([]);

  const [chatHistory, setChatHistory] = useState<ChatMessage[]>([
    {
      id: 'welcome_1',
      sender: 'bot',
      text: '🕉️ *Namaste! Welcome to Vedanga Jyotisha WhatsApp AI Jyotish.*\n\nI can automatically create your *Vedic Kundali* and answer life questions right here on WhatsApp.\n\n*To create a Kundali, send:* \nName: [Your Name]\nDOB: [DD/MM/YYYY]\nTime: [HH:MM AM/PM]\nPlace: [City, State]',
      time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    }
  ]);

  // Fetch status on mount
  useEffect(() => {
    if (isOpen) {
      fetchStatus();
      fetchLogs();
    }
  }, [isOpen]);

  const fetchStatus = async () => {
    try {
      const res = await fetch('/api/whatsapp/status');
      if (res.ok) {
        const data = await res.json();
        setStatus(data);
      }
    } catch (err) {
      console.error('Failed to fetch WhatsApp status:', err);
    }
  };

  const fetchLogs = async () => {
    try {
      const res = await fetch('/api/whatsapp/logs');
      if (res.ok) {
        const data = await res.json();
        setLogs(data.logs || []);
      }
    } catch (err) {
      console.error('Failed to fetch WhatsApp logs:', err);
    }
  };

  if (!isOpen) return null;

  const handleCopy = (text: string, fieldName: string) => {
    navigator.clipboard.writeText(text);
    setCopiedField(fieldName);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const handleSendSimulatedMessage = async (customText?: string) => {
    const textToSend = customText || inputMsg;
    if (!textToSend.trim() || loading) return;

    const userMsg: ChatMessage = {
      id: 'usr_' + Date.now(),
      sender: 'user',
      text: textToSend,
      time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };

    setChatHistory(prev => [...prev, userMsg]);
    if (!customText) setInputMsg('');
    setLoading(true);

    try {
      const res = await fetch('/api/whatsapp/simulate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          from: '+91 98765 43210',
          message: textToSend
        })
      });

      const data = await res.json();
      if (data.reply) {
        const botMsg: ChatMessage = {
          id: 'bot_' + Date.now(),
          sender: 'bot',
          text: data.reply,
          time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        };
        setChatHistory(prev => [...prev, botMsg]);
        fetchLogs();
      } else {
        throw new Error(data.error || 'Failed to get AI reply');
      }
    } catch (err: any) {
      const errorMsg: ChatMessage = {
        id: 'err_' + Date.now(),
        sender: 'bot',
        text: `⚠️ *Error*: ${err.message || 'Could not connect to Vedanga Jyotisha AI server.'}`,
        time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      };
      setChatHistory(prev => [...prev, errorMsg]);
    } finally {
      setLoading(false);
    }
  };

  const fillKundaliSample = () => {
    if (currentKundali) {
      const b = currentKundali.birthDetails;
      const text = `Name: ${b.name}\nDOB: ${b.date}\nTime: ${b.time}\nPlace: ${b.placeName}\nPlease create my Kundali and analyze my Career and Wealth Yogas.`;
      handleSendSimulatedMessage(text);
    } else {
      const text = `Name: Anjali Sharma\nDOB: 18/09/1994\nTime: 08:30 AM\nPlace: New Delhi\nPlease generate my birth chart and tell me about my current Dasha.`;
      handleSendSimulatedMessage(text);
    }
  };

  const formatWhatsAppText = (text: string) => {
    // Convert *bold* to <strong>
    const formatted = text
      .replace(/\*(.*?)\*/g, '<strong>$1</strong>')
      .replace(/\n/g, '<br/>');
    return { __html: formatted };
  };

  const getDirectWhatsAppShareLink = () => {
    if (!currentKundali) return 'https://wa.me/?text=Discover%20Vedanga%20Jyotisha%20Vedic%20Astrology';
    const b = currentKundali.birthDetails;
    const shareText = `🕉️ *Vedanga Jyotisha Kundali Chart*\n*Name*: ${b.name}\n*Lagna*: ${currentKundali.houses[0]?.sign || 'Aries'}\n*Moon Sign*: ${currentKundali.planets.find(p => p.name === 'Moon')?.sign || 'Taurus'}\n\nAsk Acharya AI Astrologer questions about this chart!`;
    return `https://wa.me/?text=${encodeURIComponent(shareText)}`;
  };

  return (
    <div 
      className="fixed inset-0 z-50 bg-astro-text/40 backdrop-blur-sm flex items-center justify-center p-3 sm:p-6 overflow-y-auto"
      onClick={onClose}
    >
      <div 
        className="bg-astro-bg border border-astro-border rounded-2xl w-full max-w-4xl max-h-[92vh] flex flex-col shadow-2xl overflow-hidden text-astro-text"
        onClick={(e) => e.stopPropagation()}
      >
        
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 bg-astro-card border-b border-astro-border">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-astro-primary/10 border border-astro-primary/20 flex items-center justify-center text-astro-primary">
              <MessageSquare className="w-5 h-5" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-bold text-lg text-astro-text font-serif">
                  WhatsApp AI Jyotish & Kundali Agent
                </h3>
                <span className="px-2.5 py-0.5 text-[10px] uppercase tracking-wider font-bold bg-amber-50 text-amber-800 border border-amber-200 rounded-full">
                  Future Dev Roadmap
                </span>
                <span className="px-2 py-0.5 text-[10px] uppercase tracking-wider font-semibold bg-[#6F9690]/15 text-astro-primary border border-[#6F9690]/30 rounded-full">
                  Sandbox Active
                </span>
              </div>
              <p className="text-xs text-astro-secondary-text">
                Preserved for future omnichannel development • Auto-generate Kundalis and answer astrological questions directly on WhatsApp
              </p>
            </div>
          </div>

          <button
            onClick={onClose}
            className="p-2 text-astro-secondary-text hover:text-astro-text rounded-lg hover:bg-astro-border/50 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Navigation Tabs */}
        <div className="flex items-center justify-between px-6 bg-astro-card border-b border-astro-border">
          <div className="flex items-center gap-1">
            <button
              onClick={() => setActiveTab('sandbox')}
              className={`flex items-center gap-2 px-4 py-3 text-xs font-semibold border-b-2 transition-colors ${
                activeTab === 'sandbox'
                  ? 'border-astro-primary text-astro-primary bg-astro-bg'
                  : 'border-transparent text-astro-secondary-text hover:text-astro-text'
              }`}
            >
              <Smartphone className="w-4 h-4" />
              WhatsApp AI Sandbox Chat
            </button>
            <button
              onClick={() => setActiveTab('config')}
              className={`flex items-center gap-2 px-4 py-3 text-xs font-semibold border-b-2 transition-colors ${
                activeTab === 'config'
                  ? 'border-astro-primary text-astro-primary bg-astro-bg'
                  : 'border-transparent text-astro-secondary-text hover:text-astro-text'
              }`}
            >
              <Settings className="w-4 h-4" />
              Webhook & API Setup
            </button>
            <button
              onClick={() => setActiveTab('logs')}
              className={`flex items-center gap-2 px-4 py-3 text-xs font-semibold border-b-2 transition-colors ${
                activeTab === 'logs'
                  ? 'border-astro-primary text-astro-primary bg-astro-bg'
                  : 'border-transparent text-astro-secondary-text hover:text-astro-text'
              }`}
            >
              <Code className="w-4 h-4" />
              Live Webhook Logs ({logs.length})
            </button>
          </div>

          <a
            href={getDirectWhatsAppShareLink()}
            target="_blank"
            rel="noopener noreferrer"
            className="hidden sm:flex items-center gap-1.5 text-xs bg-astro-primary hover:bg-[#123d3c] text-white border border-astro-primary px-3 py-1.5 rounded-lg transition-colors font-medium shadow-sm"
          >
            <ExternalLink className="w-3.5 h-3.5 text-astro-accent" />
            Share to WhatsApp
          </a>
        </div>

        {/* Tab Contents */}
        <div className="flex-1 overflow-y-auto p-6 bg-astro-bg">
          {activeTab === 'sandbox' && (
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 h-full">
              {/* WhatsApp Phone Mockup */}
              <div className="lg:col-span-7 bg-astro-card rounded-2xl border border-astro-border flex flex-col h-[520px] shadow-sm overflow-hidden">
                {/* Mock Phone Header */}
                <div className="bg-astro-primary px-4 py-3 flex items-center justify-between border-b border-[#123d3c] text-white">
                  <div className="flex items-center gap-3">
                    <div className="relative w-9 h-9 rounded-full bg-[#6F9690]/30 border border-astro-bg/30 flex items-center justify-center text-astro-bg font-bold text-sm">
                      <Bot className="w-5 h-5 text-astro-bg" />
                      <span className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-emerald-400 rounded-full border-2 border-astro-primary" />
                    </div>
                    <div>
                      <h4 className="text-sm font-bold text-astro-bg leading-none">Acharya Vedic AI</h4>
                      <span className="text-[10px] text-astro-border font-sans">Official WhatsApp Business AI</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] bg-white/10 px-2 py-0.5 rounded text-astro-bg font-mono">
                      WhatsApp Cloud
                    </span>
                  </div>
                </div>

                {/* Chat Messages Body */}
                <div className="flex-1 p-4 overflow-y-auto space-y-3 bg-astro-card">
                  {chatHistory.map(msg => (
                    <div
                      key={msg.id}
                      className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-[85%] rounded-xl px-3.5 py-2.5 text-xs shadow-sm ${
                          msg.sender === 'user'
                            ? 'bg-astro-primary text-white rounded-tr-none'
                            : 'bg-astro-bg text-astro-text rounded-tl-none border border-astro-border'
                        }`}
                      >
                        <div
                          className="leading-relaxed whitespace-pre-line font-sans"
                          dangerouslySetInnerHTML={formatWhatsAppText(msg.text)}
                        />
                        <div className={`text-[9px] text-right mt-1 font-mono ${msg.sender === 'user' ? 'text-astro-border' : 'text-astro-secondary-text'}`}>
                          {msg.time} {msg.sender === 'user' && '✓✓'}
                        </div>
                      </div>
                    </div>
                  ))}

                  {loading && (
                    <div className="flex justify-start">
                      <div className="bg-astro-bg border border-astro-border rounded-xl px-4 py-2.5 text-xs text-astro-primary flex items-center gap-2">
                        <RefreshCw className="w-3.5 h-3.5 animate-spin text-astro-accent" />
                        Acharya AI is analyzing birth chart & planetary Yogas...
                      </div>
                    </div>
                  )}
                </div>

                {/* Input Controls */}
                <div className="p-3 bg-astro-bg border-t border-astro-border flex items-center gap-2">
                  <input
                    type="text"
                    value={inputMsg}
                    onChange={e => setInputMsg(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleSendSimulatedMessage()}
                    placeholder="Type Name, DOB, Time, Place OR Ask a question..."
                    className="flex-1 bg-astro-card text-astro-text text-xs px-4 py-2.5 rounded-lg border border-astro-border focus:border-astro-primary focus:outline-none placeholder-[#4B5956]"
                  />
                  <button
                    onClick={() => handleSendSimulatedMessage()}
                    disabled={loading || !inputMsg.trim()}
                    className="bg-astro-primary hover:bg-[#123d3c] disabled:opacity-50 text-white p-2.5 rounded-lg transition-colors"
                  >
                    <Send className="w-4 h-4 text-astro-accent" />
                  </button>
                </div>
              </div>

              {/* Controls & Presets Panel */}
              <div className="lg:col-span-5 space-y-4">
                <div className="bg-astro-card border border-astro-border p-4 rounded-xl space-y-3">
                  <h4 className="text-xs font-bold text-astro-primary uppercase tracking-wider flex items-center gap-2 font-serif">
                    <Sparkles className="w-4 h-4 text-astro-accent" />
                    Quick Actions & Test Presets
                  </h4>

                  <button
                    onClick={fillKundaliSample}
                    className="w-full text-left p-3 rounded-lg bg-astro-bg hover:bg-astro-bg/80 border border-astro-border transition-all text-xs text-astro-primary font-medium flex items-center justify-between group shadow-sm"
                  >
                    <div>
                      <span className="font-bold block text-astro-text">
                        {currentKundali ? `Test with Active Chart (${currentKundali.birthDetails.name})` : 'Send Sample Birth Details'}
                      </span>
                      <span className="text-[10px] text-astro-secondary-text">Auto-sends birth parameters to test Kundali extraction</span>
                    </div>
                    <Zap className="w-4 h-4 text-astro-accent group-hover:scale-110 transition-transform" />
                  </button>

                  <div className="space-y-1.5 pt-2">
                    <p className="text-[11px] text-astro-secondary-text font-semibold">Try Astrological Queries:</p>
                    <button
                      onClick={() => handleSendSimulatedMessage('When will I get a career promotion or new job? Please analyze my 10th house.')}
                      className="w-full text-left px-3 py-2 rounded bg-astro-bg hover:bg-astro-bg/90 text-[11px] text-astro-text border border-astro-border transition-colors shadow-xs"
                    >
                      "When will I get a career promotion or job transition?"
                    </button>
                    <button
                      onClick={() => handleSendSimulatedMessage('Can you tell me about my marriage timing and spouse traits?')}
                      className="w-full text-left px-3 py-2 rounded bg-astro-bg hover:bg-astro-bg/90 text-[11px] text-astro-text border border-astro-border transition-colors shadow-xs"
                    >
                      "Analyze marriage timing and 7th House Jupiter placement."
                    </button>
                    <button
                      onClick={() => handleSendSimulatedMessage('Which gemstone and daily Mantra should I chant for mental peace and prosperity?')}
                      className="w-full text-left px-3 py-2 rounded bg-astro-bg hover:bg-astro-bg/90 text-[11px] text-astro-text border border-astro-border transition-colors shadow-xs"
                    >
                      "Suggest gemstone and Vedic remedies for prosperity."
                    </button>
                  </div>
                </div>

                <div className="bg-astro-card border border-astro-border p-4 rounded-xl space-y-2">
                  <h4 className="text-xs font-bold text-astro-primary flex items-center gap-2 font-serif">
                    <ShieldCheck className="w-4 h-4 text-astro-primary" />
                    Integration Highlights
                  </h4>
                  <ul className="text-[11px] text-astro-text space-y-1.5 list-disc list-inside">
                    <li><strong className="text-astro-primary">NLP Details Extractor</strong>: Parses name, birth date, time, and city natively.</li>
                    <li><strong className="text-astro-primary">Vedic Calculation Engine</strong>: Generates Lagna, Rashi, Nakshatra & Dasha automatically.</li>
                    <li><strong className="text-astro-primary">Gemini Jyotish Core</strong>: Powered by real-time Jyotish Acharya system instructions.</li>
                    <li><strong className="text-astro-primary">Meta Cloud API</strong>: Direct HTTP webhook callback integration ready.</li>
                  </ul>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'config' && (
            <div className="space-y-6 max-w-3xl mx-auto">
              {/* Webhook Endpoint Info */}
              <div className="bg-astro-card border border-astro-border p-5 rounded-2xl space-y-4">
                <div className="flex items-start justify-between">
                  <div>
                    <h4 className="font-bold text-sm text-astro-text flex items-center gap-2 font-serif">
                      <Globe className="w-4 h-4 text-astro-primary" />
                      Meta WhatsApp Webhook Configuration
                    </h4>
                    <p className="text-xs text-astro-secondary-text mt-0.5">
                      Enter these exact parameters in Meta for Developers &gt; WhatsApp &gt; Configuration &gt; Webhook.
                    </p>
                  </div>
                  <span className="px-2.5 py-1 text-[10px] font-bold bg-astro-primary/10 text-astro-primary rounded-md border border-astro-primary/20">
                    HTTPS Webhook Ready
                  </span>
                </div>

                {/* Callback URL */}
                <div className="space-y-1.5">
                  <label className="text-[11px] text-astro-text font-semibold uppercase tracking-wider">
                    Callback URL
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      readOnly
                      value={status?.webhookUrl || 'https://.../api/whatsapp/webhook'}
                      className="flex-1 bg-astro-bg text-astro-primary font-mono text-xs px-3.5 py-2.5 rounded-lg border border-astro-border focus:outline-none"
                    />
                    <button
                      onClick={() => handleCopy(status?.webhookUrl || '', 'url')}
                      className="px-3.5 py-2.5 bg-astro-bg hover:bg-astro-card text-xs font-semibold text-astro-text rounded-lg border border-astro-border flex items-center gap-1.5 transition-colors shadow-xs"
                    >
                      <Copy className="w-3.5 h-3.5 text-astro-accent" />
                      {copiedField === 'url' ? 'Copied!' : 'Copy'}
                    </button>
                  </div>
                </div>

                {/* Verify Token */}
                <div className="space-y-1.5">
                  <label className="text-[11px] text-astro-text font-semibold uppercase tracking-wider">
                    Verify Token
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      readOnly
                      value={status?.verifyToken || 'astroveda_whatsapp_secret'}
                      className="flex-1 bg-astro-bg text-astro-accent font-mono font-bold text-xs px-3.5 py-2.5 rounded-lg border border-astro-border focus:outline-none"
                    />
                    <button
                      onClick={() => handleCopy(status?.verifyToken || 'astroveda_whatsapp_secret', 'token')}
                      className="px-3.5 py-2.5 bg-astro-bg hover:bg-astro-card text-xs font-semibold text-astro-text rounded-lg border border-astro-border flex items-center gap-1.5 transition-colors shadow-xs"
                    >
                      <Copy className="w-3.5 h-3.5 text-astro-accent" />
                      {copiedField === 'token' ? 'Copied!' : 'Copy'}
                    </button>
                  </div>
                </div>
              </div>

              {/* Environment Variables Instructions */}
              <div className="bg-astro-card border border-astro-border p-5 rounded-2xl space-y-4">
                <h4 className="font-bold text-sm text-astro-text flex items-center gap-2 font-serif">
                  <Code className="w-4 h-4 text-astro-primary" />
                  Meta WhatsApp Business API Credentials (.env)
                </h4>
                <p className="text-xs text-astro-secondary-text">
                  To send real outgoing WhatsApp messages to live phone numbers, configure these variables in your project secrets or `.env`:
                </p>

                <div className="bg-astro-bg p-4 rounded-xl font-mono text-xs text-astro-text border border-astro-border space-y-2">
                  <div>
                    <span className="text-astro-secondary-text"># Permanent Meta Access Token with whatsapp_business_messaging scope</span>
                    <br />
                    <span className="text-astro-primary font-semibold">WHATSAPP_TOKEN</span>=<span className="text-[#6F9690]">"EAAG..."</span>
                  </div>
                  <div className="pt-1">
                    <span className="text-astro-secondary-text"># Meta Phone Number ID (from WhatsApp API Setup page)</span>
                    <br />
                    <span className="text-astro-primary font-semibold">WHATSAPP_PHONE_NUMBER_ID</span>=<span className="text-[#6F9690]">"1092837482910"</span>
                  </div>
                  <div className="pt-1">
                    <span className="text-astro-secondary-text"># Custom secret string matching Meta Webhook Verify Token</span>
                    <br />
                    <span className="text-astro-primary font-semibold">WHATSAPP_VERIFY_TOKEN</span>=<span className="text-[#6F9690]">"astroveda_whatsapp_secret"</span>
                  </div>
                </div>

                <div className="flex items-center gap-3 p-3 bg-astro-accent/15 border border-astro-accent/30 rounded-xl text-astro-text text-xs">
                  <AlertCircle className="w-5 h-5 flex-shrink-0 text-astro-accent" />
                  <span>
                    <strong>Note:</strong> Even without Meta credentials, the app runs in <strong>Sandbox Mode</strong> where you can fully test and demonstrate WhatsApp AI Kundali generation in the Sandbox tab!
                  </span>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'logs' && (
            <div className="space-y-4 max-w-3xl mx-auto">
              <div className="flex items-center justify-between">
                <h4 className="font-bold text-sm text-astro-text flex items-center gap-2 font-serif">
                  <Code className="w-4 h-4 text-astro-primary" />
                  WhatsApp Incoming & Outgoing Activity Stream
                </h4>
                <button
                  onClick={fetchLogs}
                  className="px-3 py-1.5 bg-astro-bg hover:bg-astro-card text-xs text-astro-text rounded-lg flex items-center gap-1.5 border border-astro-border shadow-xs"
                >
                  <RefreshCw className="w-3.5 h-3.5 text-astro-accent" />
                  Refresh Logs
                </button>
              </div>

              {logs.length === 0 ? (
                <div className="text-center py-12 bg-astro-card border border-astro-border rounded-xl text-astro-secondary-text text-xs">
                  No WhatsApp transactions logged yet. Test a message in the Sandbox tab!
                </div>
              ) : (
                <div className="space-y-3">
                  {logs.map((item: any) => (
                    <div key={item.id} className="bg-astro-card border border-astro-border p-4 rounded-xl space-y-2">
                      <div className="flex items-center justify-between text-xs">
                        <span className="font-mono text-astro-primary font-bold">{item.from}</span>
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] bg-[#6F9690]/20 text-astro-primary border border-[#6F9690]/30 px-2 py-0.5 rounded font-semibold">
                            {item.status}
                          </span>
                          <span className="text-[10px] text-astro-secondary-text font-mono">
                            {formatDateTime(item.timestamp)}
                          </span>
                        </div>
                      </div>

                      <div className="bg-astro-bg p-2.5 rounded-lg border border-astro-border text-xs text-astro-text font-mono">
                        <strong className="text-astro-primary">User:</strong> {item.userMessage}
                      </div>

                      <div className="bg-astro-bg p-2.5 rounded-lg border border-astro-border text-xs text-astro-text font-mono whitespace-pre-wrap">
                        <strong className="text-[#6F9690]">AI Response:</strong>
                        <div className="mt-1 font-sans" dangerouslySetInnerHTML={formatWhatsAppText(item.aiResponse)} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

      </div>
    </div>
  );
};
